import React, { useContext, useEffect, useState } from 'react';

import { media } from 'context';
import Checkbox from 'ds/Checkbox';
import IconButton from 'ds/IconButton';
import Layout from 'ds/Layout';
import Modal from 'ds/Modal';
import PillButton from 'ds/PillButton';
import Select from 'ds/Select';
import Text from 'ds/Text';
import Toggle from 'ds/Toggle';
import UserComboBox from 'ds/UserComboBox';
import { SearchableUser } from 'ds/UserComboBox/types';
import { ANIMATION_DURATION } from 'ds/constants';
import EmailInput from 'ds/inputs/EmailInput';
import PhoneInput from 'ds/inputs/PhoneInput';
import TextInput from 'ds/inputs/TextInput';
import { RequestState } from 'ds/types';
import { formatUserName } from 'ds/utils';
import { validateEmail } from 'shared';

import AddUserResult from './AddUserResult';
import { AddUsersModalProps, AddedUser } from './types';

const normalizeUser = (user: Partial<AddedUser>): AddedUser =>
  ({
    ...user,
    first: user.first || user.firstname || '',
    last: user.last || user.lastname || '',
    firstname: user.firstname || user.first || '',
    lastname: user.lastname || user.last || '',
    email: user.email || ''
  }) as AddedUser;

const createAddedUserFromSearchable = (user: SearchableUser): AddedUser => ({
  id: user.id,
  email: user.email,
  first: user.first,
  last: user.last,
  avatar_url: user.avatar_url
});

const prepareNewUserFromSearchable = (user: SearchableUser): Partial<AddedUser> => ({
  id: user.id,
  email: user.email,
  first: user.first,
  last: user.last,
  firstname: user.first,
  lastname: user.last,
  avatar_url: user.avatar_url,
  ...(user.phone != null && user.phone !== '' ? { phone: user.phone } : {}),
  ...(user.calling_code != null && user.calling_code !== '' ? { calling_code: user.calling_code } : {})
});

const getEmailDisclaimerMessage = (newUsersCount: number): string => {
  if (newUsersCount === 0) {
    return 'Adding a person will trigger an email introduction to Codi.';
  } else if (newUsersCount === 1) {
    return 'Adding this person will trigger an email introduction to Codi.';
  } else {
    return 'Adding these people will trigger an email introduction to Codi.';
  }
};

const AddUsersModal: React.FC<AddUsersModalProps> = ({
  isVisible,
  setIsVisible,
  onSuccess,
  users,
  existingUsers = [],
  editUser,
  mode = 'single',
  sendsEmail = false,
  fields = {},
  showExistingUsersInSummary = false,
  validateEmail: customValidateEmail,
  onInvite,
  onSaveWithNoNewUsers,
  onDelete,
  text,
  showId
}) => {
  const header = text?.header;
  const description = text?.description || 'Select or create a user and select permissions to add them.';
  const placeholder = text?.placeholder;
  const { xs } = useContext(media);
  const [requestState, setRequestState] = useState<RequestState>('ready');
  const [isCreatingUser, setIsCreatingUser] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<AddedUser[]>([]);
  const [newUser, setNewUser] = useState<Partial<AddedUser>>({});
  const [showFormFields, setShowFormFields] = useState<boolean>(false);
  const [editUserAdmin, setEditUserAdmin] = useState<boolean>(editUser?.admin || false);
  const [editUserTitle, setEditUserTitle] = useState<string | undefined>(editUser?.title);
  const [editUserRoles, setEditUserRoles] = useState<string[]>(editUser?.roles || []);
  const [editUserPhone, setEditUserPhone] = useState<string | undefined>(editUser?.phone);
  const [editUserCallingCode, setEditUserCallingCode] = useState<string | undefined>(editUser?.calling_code || '1');
  const [originalPhone, setOriginalPhone] = useState<string | undefined>();
  const [selectedUserIdInComboBox, setSelectedUserIdInComboBox] = useState<number | undefined>();
  const [localExistingUsers, setLocalExistingUsers] = useState<AddedUser[]>(existingUsers);
  const [pendingDeletions, setPendingDeletions] = useState<Set<number>>(new Set());

  const { phone: showPhone, role: showRole, roles: showRoles, title: showTitle, admin: showAdmin } = fields;
  const isMultipleMode = mode === 'multiple';
  const hasOptionalFields = showPhone || showAdmin || showRole || showTitle || showRoles;
  const allowDeletion = showExistingUsersInSummary && !!onDelete;

  const resetFormState = () => {
    setNewUser({});
    setShowFormFields(false);
    setOriginalPhone(undefined);
    setSelectedUserIdInComboBox(undefined);
  };

  const initializeFormState = (showFields: boolean) => {
    setIsCreatingUser(true);
    resetFormState();
    setShowFormFields(showFields);
  };

  const getAvailableUsersCount = () => {
    return users.length === 0 ? 0 : users.filter(u => !existingUsers.some(eu => eu.id === u.id)).length;
  };

  const initializeToForm = () => {
    setIsCreatingUser(true);
    setShowFormFields(true);
    setNewUser({});
    setOriginalPhone(undefined);
    setSelectedUserIdInComboBox(undefined);
  };

  const initializeToFormOrCombobox = () => {
    const availableUsersCount = getAvailableUsersCount();
    if (availableUsersCount === 0) {
      initializeToForm();
    } else {
      initializeFormState(false);
    }
  };

  useEffect(() => {
    if (editUser) {
      setEditUserAdmin(editUser.admin || false);
      setEditUserTitle(editUser.title);
      setEditUserRoles(editUser.roles || []);
      setEditUserPhone(editUser.phone);
      setEditUserCallingCode(editUser.calling_code || '1');
    }
  }, [editUser]);

  useEffect(() => {
    if (isVisible) {
      setLocalExistingUsers(existingUsers);
    }
  }, [isVisible, existingUsers]);

  useEffect(() => {
    if (!isVisible) {
      setTimeout(() => {
        setIsCreatingUser(false);
        setSelectedUsers([]);
        resetFormState();
        setEditUserTitle(undefined);
        setEditUserRoles([]);
        setLocalExistingUsers(existingUsers);
        setPendingDeletions(new Set());
      }, ANIMATION_DURATION);
      return;
    }

    if (editUser) {
      setIsCreatingUser(false);
      return;
    }

    if (isMultipleMode) {
      if (showExistingUsersInSummary && existingUsers.length > 0) {
        setIsCreatingUser(false);
        resetFormState();
        return;
      }
      initializeToFormOrCombobox();
      return;
    }

    if (sendsEmail) {
      const isSupportTicket = users.length === 0 && localExistingUsers.length === 0;
      initializeFormState(isSupportTicket);
      return;
    }

    initializeToFormOrCombobox();
  }, [isVisible, isMultipleMode, sendsEmail, localExistingUsers.length, editUser, users.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeAndReset = () => {
    setIsVisible(false);
  };

  const handleUserSelect = (user: SearchableUser | undefined) => {
    if (!user) {
      setSelectedUserIdInComboBox(undefined);
      return;
    }

    setSelectedUserIdInComboBox(user.id);
    const addedUser = createAddedUserFromSearchable(user);

    if (isMultipleMode) {
      if (hasOptionalFields) {
        setNewUser(prepareNewUserFromSearchable(user));
        setOriginalPhone(user.phone ?? undefined);
        setIsCreatingUser(true);
        setShowFormFields(true);
        setSelectedUserIdInComboBox(undefined);
      } else {
        setSelectedUsers([...selectedUsers, addedUser]);
        setIsCreatingUser(false);
        setSelectedUserIdInComboBox(undefined);
      }
    } else if (sendsEmail) {
      if (hasOptionalFields) {
        setNewUser(prepareNewUserFromSearchable(user));
        setOriginalPhone(user.phone ?? undefined);
        setIsCreatingUser(true);
        setShowFormFields(true);
        setSelectedUserIdInComboBox(undefined);
      } else {
        setSelectedUsers([addedUser]);
        setIsCreatingUser(false);
      }
    } else {
      if (hasOptionalFields) {
        setNewUser(prepareNewUserFromSearchable(user));
        setOriginalPhone(user.phone ?? undefined);
        setIsCreatingUser(true);
        setShowFormFields(true);
        setSelectedUserIdInComboBox(undefined);
      } else {
        setSelectedUserIdInComboBox(undefined);
        handleSubmit([addedUser]);
      }
    }
  };

  const handleCreateNew = (query?: string) => {
    if (query?.includes('@')) {
      setNewUser({ email: query });
    } else if (query) {
      const [first, last] = query.split(' ');
      setNewUser({ firstname: first, first: first, lastname: last, last: last || '' });
    }
    setIsCreatingUser(true);
    setShowFormFields(true);
    setOriginalPhone(undefined);
    setSelectedUserIdInComboBox(undefined);
  };

  const handleBackToSearch = () => {
    resetFormState();
  };

  const handleAddUser = () => {
    const {
      id,
      email,
      firstname: _firstname,
      first,
      lastname: _lastname,
      last,
      phone,
      calling_code,
      admin,
      role,
      title,
      roles,
      avatar_url
    } = newUser;

    if (!email || !first || !last) return;

    const emailValidation = customValidateEmail ? customValidateEmail(email) : validateEmail(email, true);

    if (!emailValidation.valid) {
      return;
    }

    const addedUser: AddedUser = {
      ...(id ? { id } : {}),
      email,
      first,
      last,
      firstname: first,
      lastname: last,
      phone,
      calling_code: calling_code || '1',
      admin,
      role: role || title,
      title,
      roles,
      ...(avatar_url ? { avatar_url } : {})
    };

    if (isMultipleMode) {
      setSelectedUsers([...selectedUsers, addedUser]);
      setIsCreatingUser(false);
      resetFormState();
    } else if (sendsEmail) {
      setSelectedUsers([addedUser]);
      setIsCreatingUser(false);
      resetFormState();
    } else {
      setSelectedUserIdInComboBox(undefined);
      handleSubmit([addedUser]);
    }
  };

  const handleSubmit = async (usersToInvite: AddedUser[] = selectedUsers) => {
    if (!usersToInvite.length && pendingDeletions.size === 0) {
      if (onSaveWithNoNewUsers) {
        setRequestState('in_progress');
        try {
          await onSaveWithNoNewUsers();
          onSuccess();
          closeAndReset();
        } catch {
        } finally {
          setRequestState('ready');
        }
        return;
      }
      closeAndReset();
      return;
    }

    setRequestState('in_progress');

    try {
      if (usersToInvite.length > 0) {
        await onInvite(usersToInvite);
      }

      if (pendingDeletions.size > 0 && onDelete) {
        for (const userId of pendingDeletions) {
          try {
            await onDelete(userId);
          } catch (error) {}
        }
      }

      onSuccess();
      closeAndReset();
    } catch (error) {
    } finally {
      setRequestState('ready');
    }
  };

  const handleDelete = (userId: number) => {
    if (!onDelete || !allowDeletion) return;

    setPendingDeletions(prev => {
      const newSet = new Set(prev);
      newSet.add(userId);

      if (!isCreatingUser && !isEditMode && (isMultipleMode || sendsEmail)) {
        const hasRemainingExistingUsers = showExistingUsersInSummary
          ? localExistingUsers.some(u => u.id !== undefined && !newSet.has(u.id))
          : false;
        const hasRemainingUsers = selectedUsers.length > 0 || hasRemainingExistingUsers;

        if (!hasRemainingUsers) {
          setTimeout(() => {
            setIsCreatingUser(false);
            resetFormState();
          }, 0);
        }
      }

      return newSet;
    });
  };

  const handleUndoDelete = (userId: number) => {
    setPendingDeletions(prev => {
      const newSet = new Set(prev);
      newSet.delete(userId);
      return newSet;
    });
  };

  const handleRemoveSelectedUser = (index: number) => {
    const updatedSelectedUsers = selectedUsers.filter((_, i) => i !== index);
    setSelectedUsers(updatedSelectedUsers);

    if (!isCreatingUser && !isEditMode && (isMultipleMode || sendsEmail)) {
      const hasRemainingExistingUsers = showExistingUsersInSummary
        ? localExistingUsers.some(u => u.id !== undefined && !pendingDeletions.has(u.id))
        : false;
      const hasRemainingUsers = updatedSelectedUsers.length > 0 || hasRemainingExistingUsers;

      if (!hasRemainingUsers) {
        setIsCreatingUser(false);
        resetFormState();
      }
    }
  };

  const handleEditSave = async () => {
    if (!editUser) return;

    setRequestState('in_progress');
    try {
      await onInvite([
        {
          ...editUser,
          admin: editUserAdmin,
          title: editUserTitle,
          roles: editUserRoles,
          phone: editUserPhone,
          calling_code: editUserCallingCode
        }
      ]);
      onSuccess();
      closeAndReset();
    } catch (error) {
    } finally {
      setRequestState('ready');
    }
  };

  const { email, firstname, first, lastname, last, phone, calling_code, admin, role, title, roles } = newUser;
  const currentFirst = first || firstname || '';
  const currentLast = last || lastname || '';

  const emailValidation = email
    ? customValidateEmail
      ? customValidateEmail(email)
      : validateEmail(email, true)
    : { valid: false };

  const isDuplicateEmail = email
    ? localExistingUsers.some(u => u.email?.toLowerCase() === email.toLowerCase()) ||
      selectedUsers.some(u => u.email?.toLowerCase() === email.toLowerCase())
    : false;

  const newUserIsValid =
    !!email &&
    emailValidation.valid &&
    !isDuplicateEmail &&
    !!currentFirst &&
    !!currentLast &&
    (!showRole || (typeof showRole === 'object' ? !!role || !!title : true)) &&
    (!showTitle || !!title) &&
    (!showRoles || (roles && roles.length > 0));

  const isEditMode = !!editUser;
  const isInCreationForm = isCreatingUser && !isEditMode;
  const isInConfirmationView = !isCreatingUser && (isMultipleMode || sendsEmail) && !isEditMode;

  const availableUsers: SearchableUser[] = users.map(u => {
    const isExisting = localExistingUsers.some(eu => eu.id === u.id);
    const isSelected = selectedUsers.some(su => su.id === u.id) && u.id !== selectedUserIdInComboBox;

    if (isExisting) {
      return {
        ...u,
        disabled: true,
        rightElement: (
          <Text size="body-xs" color="gray-500">
            Already added
          </Text>
        )
      };
    }

    if (isSelected) {
      return {
        ...u,
        disabled: true
      };
    }

    return u;
  });

  const hasNewUsers = selectedUsers.some(u => !u.id);
  const shouldShowList = isMultipleMode || (sendsEmail && selectedUsers.length > 0 && hasNewUsers);
  const hasUsersToDisplay = (showExistingUsersInSummary && localExistingUsers.length > 0) || selectedUsers.length > 0;
  const newUsersCount = selectedUsers.filter(user => !user.id).length;
  const shouldShowEmailDisclaimer = sendsEmail && hasNewUsers;

  return (
    <Modal
      isVisible={isVisible}
      onClose={closeAndReset}
      paddingX={36}
      paddingBottom={16}
      hasBottomSheetPaddingX
      header={
        header ||
        (isEditMode
          ? 'Edit user'
          : isInCreationForm
            ? 'Add user'
            : isInConfirmationView
              ? 'Confirm users'
              : 'Invite users')
      }
      headerAlign="left"
      size="lg"
      bottomSheetIsRounded={true}
      bottomSheetBorderRadius={16}
      width={500}
      {...(isEditMode
        ? {
            footer: (
              <Layout direction="row" gap={12} justify="flex-end">
                <PillButton
                  text="Cancel"
                  size="lg"
                  type="secondaryOutline"
                  onClick={closeAndReset}
                  disabled={requestState === 'in_progress'}
                />
                <PillButton
                  text="Save"
                  size="lg"
                  type="purpleGradient"
                  onClick={handleEditSave}
                  disabled={requestState === 'in_progress'}
                  showSpinner={requestState === 'in_progress'}
                />
              </Layout>
            )
          }
        : isInCreationForm
          ? {
              footer: (
                <Layout direction="row" gap={12} justify={xs ? 'space-between' : 'flex-end'}>
                  {!xs && (
                    <PillButton
                      text="Cancel"
                      size="lg"
                      type="secondaryOutline"
                      onClick={closeAndReset}
                      disabled={requestState === 'in_progress'}
                    />
                  )}
                  <PillButton
                    text={isMultipleMode || sendsEmail ? 'Add user' : 'Save'}
                    size="lg"
                    type="purpleGradient"
                    onClick={handleAddUser}
                    disabled={!newUserIsValid || requestState === 'in_progress'}
                    showSpinner={requestState === 'in_progress' && !isMultipleMode && !sendsEmail}
                    fullWidth={xs}
                  />
                </Layout>
              ),
              topLeft: showFormFields ? (
                <IconButton size="md" type="noBackground" onClick={handleBackToSearch} name="leftChevron" stroke={2} />
              ) : selectedUsers.length > 0 ? (
                <IconButton
                  size="md"
                  type="noBackground"
                  onClick={() => {
                    setIsCreatingUser(false);
                    resetFormState();
                  }}
                  name="leftChevron"
                  stroke={2}
                />
              ) : showExistingUsersInSummary && localExistingUsers.length > 0 ? (
                <IconButton
                  size="md"
                  type="noBackground"
                  onClick={() => {
                    setIsCreatingUser(false);
                    resetFormState();
                  }}
                  name="leftChevron"
                  stroke={2}
                />
              ) : undefined
            }
          : {
              footer: (
                <Layout direction="row" gap={12} justify="space-between" align="center">
                  {shouldShowEmailDisclaimer ? (
                    <Text size="body-xs" color="gray-700">
                      * {getEmailDisclaimerMessage(newUsersCount)}
                    </Text>
                  ) : (
                    <Layout />
                  )}
                  <Layout direction="row" gap={12} justify="flex-end">
                    <PillButton
                      text="Cancel"
                      size="lg"
                      type="secondaryOutline"
                      onClick={closeAndReset}
                      disabled={requestState === 'in_progress'}
                    />
                    <PillButton
                      text="Save"
                      size="lg"
                      type="purpleGradient"
                      onClick={() => handleSubmit()}
                      disabled={
                        requestState === 'in_progress' ||
                        (showExistingUsersInSummary
                          ? !localExistingUsers.length && !selectedUsers.length && pendingDeletions.size === 0
                          : !selectedUsers.length && pendingDeletions.size === 0)
                      }
                      showSpinner={requestState === 'in_progress'}
                    />
                  </Layout>
                </Layout>
              )
            })}
    >
      {isEditMode ? (
        <Layout style={{ minHeight: 300 }}>
          {description && (
            <Layout marginTop={0}>
              <Text size="body-sm" color="gray-600">
                {description}
              </Text>
            </Layout>
          )}
          <Layout marginTop={description ? 24 : 0}>
            <Layout direction="row" gap={12}>
              <Layout flexGrow={1}>
                <TextInput
                  size="sm"
                  value={editUser?.firstname || editUser?.first || ''}
                  label="First Name"
                  required
                  disabled={true}
                  onChange={() => {}}
                />
              </Layout>
              <Layout flexGrow={1}>
                <TextInput
                  size="sm"
                  value={editUser?.lastname || editUser?.last || ''}
                  label="Last Name"
                  required
                  disabled={true}
                  onChange={() => {}}
                />
              </Layout>
            </Layout>
            <Layout marginTop={12}>
              <EmailInput size="sm" value={editUser?.email || ''} disabled={true} onChange={() => {}} />
            </Layout>
            {showPhone && (
              <Layout marginTop={12}>
                <PhoneInput
                  label="Phone"
                  callingCode={editUserCallingCode || '1'}
                  onCallingCodeChange={code => setEditUserCallingCode(code)}
                  value={editUserPhone}
                  size="sm"
                  disabled={!!editUser?.phone}
                  onChange={({ value }) => setEditUserPhone(value)}
                />
              </Layout>
            )}
            {showTitle && (
              <Layout marginTop={12}>
                <Select
                  label={showTitle.label || 'Title'}
                  value={editUserTitle}
                  options={showTitle.options}
                  onChange={({ value }) => setEditUserTitle(value)}
                  required
                  placeholder={`Select ${showTitle.label || 'title'}`}
                  size="sm"
                />
              </Layout>
            )}
            {showRoles && (
              <>
                <Layout marginTop={showTitle ? 24 : 24}>
                  <Text size="body-sm" semibold>
                    {showRoles.label || 'Roles'}
                  </Text>
                </Layout>
                <Layout marginTop={16} direction="row" style={{ flexWrap: 'wrap' }}>
                  {showRoles.options.map((roleOption, index) => (
                    <Layout key={roleOption.value} marginLeft={index > 0 ? 16 : undefined} marginBottom={8}>
                      <Checkbox
                        size="sm"
                        value={editUserRoles.includes(roleOption.value) ? 'checked' : 'off'}
                        label={roleOption.label}
                        onChange={({ value }) => {
                          if (value === 'checked') {
                            setEditUserRoles([...editUserRoles, roleOption.value]);
                          } else {
                            setEditUserRoles(editUserRoles.filter(r => r !== roleOption.value));
                          }
                        }}
                      />
                    </Layout>
                  ))}
                </Layout>
              </>
            )}
            {showAdmin && (
              <Layout marginTop={24}>
                <Toggle label="Admin" checked={editUserAdmin} onChange={setEditUserAdmin} size="sm" />
              </Layout>
            )}
          </Layout>
        </Layout>
      ) : isInCreationForm ? (
        <Layout style={{ minHeight: 300 }}>
          {description && (
            <Layout marginTop={0}>
              <Text size="body-sm" color="gray-600">
                {description}
              </Text>
            </Layout>
          )}
          <Layout
            marginTop={description ? 24 : 0}
            style={
              !showFormFields && !email && !currentFirst && !currentLast
                ? { position: 'relative', zIndex: 1 }
                : undefined
            }
          >
            {!showFormFields && !email && !currentFirst && !currentLast ? (
              <UserComboBox
                users={availableUsers}
                selectedUserId={selectedUserIdInComboBox}
                onSelect={handleUserSelect}
                onCreateNew={handleCreateNew}
                size="sm"
                required={false}
                showId={showId}
                placeholder={placeholder}
              />
            ) : (
              <>
                <Layout direction="row" gap={12}>
                  <Layout flexGrow={1}>
                    <TextInput
                      size="sm"
                      onChange={({ value }) => setNewUser({ ...newUser, firstname: value, first: value })}
                      value={currentFirst}
                      label="First Name"
                      autoFocus={!currentFirst}
                      required
                      disabled={!!newUser.id}
                    />
                  </Layout>
                  <Layout flexGrow={1}>
                    <TextInput
                      size="sm"
                      onChange={({ value }) => setNewUser({ ...newUser, lastname: value, last: value })}
                      value={currentLast}
                      label="Last Name"
                      required
                      disabled={!!newUser.id}
                    />
                  </Layout>
                </Layout>
                <Layout marginTop={12}>
                  <EmailInput
                    size="sm"
                    onChange={({ value }) => setNewUser({ ...newUser, email: value })}
                    value={email}
                    autoFocus={!!currentFirst && !!currentLast && !email}
                    disabled={!!newUser.id}
                    error={
                      email && !emailValidation.valid
                        ? emailValidation.error
                        : isDuplicateEmail && email
                          ? (() => {
                              const emailLower = email.toLowerCase();
                              const existingUser =
                                localExistingUsers.find(u => u.email?.toLowerCase() === emailLower) ||
                                selectedUsers.find(u => u.email?.toLowerCase() === emailLower);
                              const userName = existingUser ? formatUserName(existingUser) : 'User';
                              return `${userName} already added`;
                            })()
                          : undefined
                    }
                  />
                </Layout>
                {showFormFields && (
                  <>
                    {showPhone && (
                      <Layout marginTop={12}>
                        <PhoneInput
                          label="Phone"
                          callingCode={calling_code || '1'}
                          onCallingCodeChange={code => setNewUser({ ...newUser, calling_code: code })}
                          value={phone}
                          size="sm"
                          disabled={!!newUser.id && !!originalPhone}
                          onChange={({ value }) => setNewUser({ ...newUser, phone: value })}
                        />
                      </Layout>
                    )}
                    {showTitle && (
                      <Layout marginTop={12}>
                        <Select
                          label={showTitle.label || 'Title'}
                          value={title}
                          options={showTitle.options}
                          onChange={({ value }) => setNewUser({ ...newUser, title: value })}
                          required
                          placeholder={`Select ${showTitle.label || 'title'}`}
                          size="sm"
                        />
                      </Layout>
                    )}
                    {showRole && typeof showRole === 'object' && (
                      <Layout marginTop={12}>
                        <Select
                          label={showRole.label || 'Role'}
                          value={role || title}
                          options={showRole.options}
                          onChange={({ value }) => setNewUser({ ...newUser, role: value, title: value })}
                          required
                          placeholder={`Select ${showRole.label || 'role'}`}
                          size="sm"
                        />
                      </Layout>
                    )}
                    {showRoles && (
                      <>
                        <Layout marginTop={showTitle || showRole ? 24 : 12}>
                          <Text size="body-sm" semibold>
                            {showRoles.label || 'Roles'}
                          </Text>
                        </Layout>
                        <Layout marginTop={16} direction="row" style={{ flexWrap: 'wrap' }}>
                          {showRoles.options.map((roleOption, index) => (
                            <Layout key={roleOption.value} marginLeft={index > 0 ? 16 : undefined} marginBottom={8}>
                              <Checkbox
                                size="sm"
                                value={roles?.includes(roleOption.value) ? 'checked' : 'off'}
                                label={roleOption.label}
                                onChange={({ value }) => {
                                  const currentRoles = roles || [];
                                  if (value === 'checked') {
                                    setNewUser({ ...newUser, roles: [...currentRoles, roleOption.value] });
                                  } else {
                                    setNewUser({
                                      ...newUser,
                                      roles: currentRoles.filter(r => r !== roleOption.value)
                                    });
                                  }
                                }}
                              />
                            </Layout>
                          ))}
                        </Layout>
                      </>
                    )}
                    {showAdmin && (
                      <Layout marginTop={12}>
                        <Toggle
                          label="Admin"
                          checked={admin || false}
                          onChange={isChecked => setNewUser({ ...newUser, admin: isChecked })}
                          size="sm"
                        />
                      </Layout>
                    )}
                  </>
                )}
              </>
            )}
          </Layout>
        </Layout>
      ) : (
        <Layout style={{ minHeight: 300 }}>
          {description && (
            <Layout marginTop={0}>
              <Text size="body-sm" color="gray-600">
                {description}
              </Text>
            </Layout>
          )}
          {isInConfirmationView && (
            <>
              {users.length > 0 && !isMultipleMode && (
                <Layout marginTop={description ? 12 : 0} style={{ position: 'relative', zIndex: 1 }}>
                  <UserComboBox
                    users={availableUsers}
                    selectedUserId={selectedUserIdInComboBox}
                    onSelect={handleUserSelect}
                    onCreateNew={handleCreateNew}
                    size="sm"
                    required={false}
                    showId={showId}
                    placeholder={placeholder}
                  />
                </Layout>
              )}
              {shouldShowList && (
                <Layout marginTop={users.length > 0 ? 24 : description ? 24 : 0} direction="column" gap={0}>
                  {hasUsersToDisplay && (
                    <Layout
                      direction="column"
                      maxHeight={500}
                      overflowY="auto"
                      style={{
                        borderTop: '1px solid #eceff1',
                        ...(xs ? { borderTopLeftRadius: 16, borderTopRightRadius: 16 } : {})
                      }}
                    >
                      {showExistingUsersInSummary &&
                        localExistingUsers.map(user => {
                          const userId = user.id;
                          const isPending = userId !== undefined && pendingDeletions.has(userId);
                          return (
                            <AddUserResult
                              key={user.id || user.email}
                              user={normalizeUser(user)}
                              onDelete={allowDeletion && userId && !isPending ? () => handleDelete(userId) : undefined}
                              showDelete={allowDeletion}
                              requestInProgress={requestState === 'in_progress'}
                              isPendingDeletion={isPending}
                              onUndo={isPending && userId ? () => handleUndoDelete(userId) : undefined}
                            />
                          );
                        })}
                      {selectedUsers.map((user, index) => (
                        <AddUserResult
                          key={user.id || `${user.email}-${index}`}
                          user={normalizeUser(user)}
                          onDelete={isMultipleMode ? () => handleRemoveSelectedUser(index) : undefined}
                          showDelete={isMultipleMode}
                          requestInProgress={false}
                          showEmailIndicator={shouldShowEmailDisclaimer && !user.id}
                          isNewUser={true}
                          sendsEmail={sendsEmail}
                        />
                      ))}
                    </Layout>
                  )}
                </Layout>
              )}
              {isMultipleMode && (
                <Layout marginTop={hasUsersToDisplay ? 8 : description ? 24 : 0} direction="row" justify="flex-start">
                  <PillButton
                    type="primaryInverse"
                    size="md"
                    text="Add new user"
                    onClick={() => {
                      setIsCreatingUser(true);
                      resetFormState();
                    }}
                    icon="plus"
                    iconPosition="left"
                    iconSize="xs"
                  />
                </Layout>
              )}
            </>
          )}
        </Layout>
      )}
    </Modal>
  );
};

export default AddUsersModal;
